<template>
  <div
    v-click-outside="onClickOutside"
    class="relative"
    :class="{ 'inline-block': isPopup }"
  >
    <Popover>
      <PopoverButton
        v-if="appearance !== 'static'"
        class="w-full focus:outline-none focus:ring-0 focus:ring-offset-0"
        :disabled="disabled"
        @click="togglePopover"
      >
        <div ref="inputArea" class="relative">
          <Input
            v-if="appearance === 'input'"
            id="date"
            :model-value="computedDate"
            :prefix-icon="CalendarIcon"
            :button="
              todayButton && isTodayButtonVisible
                ? $t('general.today')
                : undefined
            "
            read-only
            :focused="showCalendar"
            :disabled="disabled"
            @focus="openCalendar"
            @button-clicked="selectToday"
          />

          <Badge
            v-else-if="appearance === 'input-badge'"
            :title="computedDate"
            :prefix-icon="CalendarIcon"
            color="gray"
            size="sm"
            :class="{ 'cursor-pointer': !disabled }"
            @click="openCalendar"
          />
        </div>
      </PopoverButton>
      <teleport :disabled="appearance === 'static'" to="body">
        <PopoverPanel v-slot="{ close }" :static="appearance === 'static'">
          <div class="hidden">{{ captureClose(close) }}</div>
          <div
            ref="calendarArea"
            class="z-60 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9"
            :class="[
              isPopup
                ? 'z-60 absolute mt-4 w-80 max-w-full rounded-xl bg-white p-2 shadow-lg ring-1 ring-black/5'
                : '',
              { invisible: appearance !== 'static' },
            ]"
            :style="calendarAreaPositionClass"
          >
            <div class="mt-2 flex items-center text-gray-900">
              <button
                type="button"
                class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                @click="previousMonth"
              >
                <span class="sr-only">Previous month</span>
                <ChevronLeftIcon class="size-5" aria-hidden="true" />
              </button>
              <div class="flex-auto text-sm font-semibold">
                {{
                  $t('components.datepicker.month-' + calendar.monthNumber())
                }}
                {{ calendar.yearNumber() }}
              </div>
              <button
                type="button"
                class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                @click="nextMonth"
              >
                <span class="sr-only">Next month</span>
                <ChevronRightIcon class="size-5" aria-hidden="true" />
              </button>
            </div>
            <div class="mt-4 grid grid-cols-7 text-xs leading-6 text-gray-900">
              <div>{{ $t('components.datepicker.monday-letter') }}</div>
              <div>{{ $t('components.datepicker.tuesday-letter') }}</div>
              <div>{{ $t('components.datepicker.wednesday-letter') }}</div>
              <div>{{ $t('components.datepicker.thursday-letter') }}</div>
              <div>{{ $t('components.datepicker.friday-letter') }}</div>
              <div>{{ $t('components.datepicker.saturday-letter') }}</div>
              <div>{{ $t('components.datepicker.sunday-letter') }}</div>
            </div>
            <div
              class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
            >
              <button
                v-for="(day, dayIdx) in days"
                :key="dayIdx"
                type="button"
                class="dayButton group relative z-20 py-1.5 hover:!bg-gray-100 focus:z-10"
                :class="[
                  !day.isDisabled ? `bg-white` : 'bg-gray-100',
                  day.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  {
                    'font-semibold': isDaySelected(day) || day.isToday,
                    'text-white': isDaySelected(day),
                    'text-gray-900':
                      !isDaySelected(day) && !day.isDisabled && !day.isToday,
                    'text-gray-300':
                      !isDaySelected(day) && day.isDisabled && !day.isToday,
                    '!bg-gray-50': day.isOutsideCurrentMonth && !day.isDisabled,
                    'text-indigo-400': day.isToday && !isDaySelected(day),
                    'rounded-tl-lg': dayIdx === 0,
                    'rounded-tr-lg': dayIdx === 6,
                    'rounded-bl-lg': dayIdx === days.length - 7,
                    'rounded-br-lg': dayIdx === days.length - 1,
                  },
                ]"
                @click="selectDate(day, dayIdx, true)"
                @pointerenter="hoverDate(day)"
              >
                <time
                  :datetime="day.date.toString()"
                  :class="[
                    'relative z-10 mx-auto flex size-7 items-center justify-center rounded-full',

                    mode === 'date-range' &&
                      isSelectingRangeSecondDate &&
                      'group-hover:bg-indigo-300',
                    isDaySelected(day) &&
                      'bg-indigo-600 group-hover:bg-indigo-600',
                    getColorPerDate(day.date) &&
                      `bg-${getColorPerDate(day.date)}-100 text-${getColorPerDate(
                        day.date
                      )}-700`,
                  ]"
                >
                  {{ day.date.getDate() }}
                </time>
                <div
                  v-if="day.isPipeLeft"
                  class="absolute top-[50%] h-[70%] w-[50%] translate-y-[-50%] bg-indigo-200/70"
                />
                <div
                  v-if="day.isPipeRight"
                  class="absolute left-[50%] top-[50%] h-[70%] w-[50%] translate-y-[-50%] bg-indigo-200/70"
                />
              </button>
            </div>

            <div v-if="mode === 'date-time'" class="mb-2 mt-4 block">
              <DatepickerTimeSelect
                v-model="selectedTime"
                :selected-date="selectedDate"
                :time-format="timeFormat"
                :max-date="maxDate"
                :min-date="minDate"
                :mode="mode"
                :disabled="disabled || isTimeDisabled"
                :can-remove-time="canRemoveTime"
              />
            </div>
          </div>
        </PopoverPanel>
      </teleport>
    </Popover>
  </div>
</template>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/8028343/Datepicker
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/20/solid';
import { set, isEqual } from 'lodash';
import { Calendar } from 'ts-headless-calendar/lib';
import type { MonthCalendar, DayCalendar } from 'ts-headless-calendar/lib';
import { klona } from 'klona';
import { flip, offset } from '@floating-ui/dom';
import { format } from '@/util/formatter/date';
import type {
  DatepickerDay,
  DatepickerSelectedDate,
  DatepickerValue,
  DatepickerColor,
  DatepickerRangeValue,
  DatepickerMode,
  DatepickerTimeFormat,
} from '@/types';
import { isSameDay, getNormalizedDate, isSameMonth } from '@/util/date-utils';
import { useSmartPositioning } from '~/composables/smart-positioning';

type DatepickerType = 'static' | 'input' | 'input-badge';

const props = defineProps({
  modelValue: {
    type: Object as PropType<DatepickerValue>,
  },
  dateRange: {
    type: Object as PropType<DatepickerRangeValue>,
  },
  appearance: {
    type: String as PropType<DatepickerType>,
    default: 'static' as DatepickerType,
  },
  mode: {
    type: String as PropType<DatepickerMode>,
    default: 'date' as DatepickerMode,
  },
  disablePast: {
    type: Boolean,
    default: false,
  },
  todayButton: {
    type: Boolean,
    default: true,
  },
  preselectedMonth: {
    default: new Date(),
    type: Date,
  },
  preselectedDate: {
    type: Object as PropType<Date | null>,
    default: null,
  },
  dayBackgroundColors: {
    type: Array as PropType<DatepickerColor[]>,
    default: () => [],
  },
  timeFormat: {
    type: String as PropType<DatepickerTimeFormat>,
    default: '24h' as DatepickerTimeFormat,
  },
  minDate: {
    type: Object as PropType<Date>,
  },
  maxDate: {
    type: Object as PropType<Date>,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isTimeDisabled: {
    type: Boolean,
    default: false,
  },
  allowSingleDayRange: {
    type: Boolean,
    default: false,
  },
  canRemoveTime: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:dateRange',
  'rangeSelected',
  'dayClicked',
]);

const i18n = useI18n();

// Model value

const value = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    if (!isEqual(props.modelValue, newValue)) {
      emit('update:modelValue', newValue);
    }
  },
});

const dateRangeValue = computed({
  get: () => props.dateRange,
  set: (newValue) => {
    if (!isEqual(props.dateRange, newValue)) {
      emit('update:dateRange', newValue);
    }
  },
});

const selectedTime = computed({
  get: () => {
    return value.value?.time ?? { h: undefined, m: undefined };
  },
  set: (newValue) => {
    const dateTemp = value.value
      ? klona(value.value)
      : { date: undefined, time: undefined };
    dateTemp.time = newValue;
    value.value = dateTemp;
  },
});

const isTodayButtonVisible = computed(() => {
  if (!props.minDate || !props.maxDate) {
    return true;
  }

  const todayDate = new Date();

  return (
    props.minDate &&
    todayDate.getTime() >= props.minDate?.getTime() &&
    props.maxDate &&
    todayDate.getTime() <= props.maxDate?.getTime()
  );
});

const inputArea = ref();

const isSecondDateAlreadySet = computed(
  () => secondSelectedDatePickerDate.value?.index !== -1
);

const calendarAreaPositionClass = computed(() => {
  if (!inputArea.value || props.appearance !== 'static') {
    return null;
  }

  const minLeftSpaceToBottom = 300;
  const { bottom, height } = inputArea.value.getBoundingClientRect();
  const marginSpace = 10;

  return window.innerHeight - minLeftSpaceToBottom >= bottom
    ? `top: ${height}px`
    : `bottom: ${height + marginSpace}px`;
});

const selectedDatePickerDate = ref<DatepickerSelectedDate>({
  index: -1,
});

// only used for mode = 'date-range'
const secondSelectedDatePickerDate = ref<DatepickerSelectedDate>({
  index: -1,
});

const selectedDate = computed<DatepickerSelectedDate>({
  get: () => {
    return selectedDatePickerDate.value;
  },
  set: (newValue: DatepickerSelectedDate) => {
    selectedDatePickerDate.value = newValue;
    const path = props.mode === 'date-range' ? ['dateFrom'] : ['date'];

    let dateTemp: DatepickerValue | DatepickerRangeValue;
    if (props.mode === 'date-range') {
      /*  Resets also the second Date of the dateRange.
          selectedDate can only be changed inside of this Datepicker-Component
          (innerValue)
          That means, that the user has picked a different dateFrom and
          therefore the dateTo needs to be resetted */
      dateTemp = { dateFrom: undefined, dateTo: undefined };
    } else {
      dateTemp = value.value
        ? klona(value.value)
        : { date: undefined, time: undefined };
    }

    if (!selectedDatePickerDate.value?.day?.date) {
      return;
    }

    set(dateTemp, path, selectedDatePickerDate.value?.day?.date);

    if (props.mode === 'date-range') {
      dateRangeValue.value = dateTemp as DatepickerRangeValue;
      return;
    }

    value.value = dateTemp as DatepickerValue;
  },
});

const secondDate = computed<DatepickerSelectedDate>({
  get: () => {
    return secondSelectedDatePickerDate.value;
  },
  set: (newValue) => {
    const path = ['dateTo'];
    secondSelectedDatePickerDate.value = newValue;

    const dateTemp = props.dateRange
      ? klona(props.dateRange)
      : { dateFrom: undefined, dateTo: undefined };

    if (!secondSelectedDatePickerDate.value?.day?.date) {
      return;
    }

    set(dateTemp, path, secondSelectedDatePickerDate.value?.day?.date);
    dateRangeValue.value = dateTemp as DatepickerRangeValue;
  },
});

const isDaySelected = (day: DatepickerDay) => {
  if (props.mode === 'date-range') {
    return (
      (dateRangeValue.value?.dateFrom &&
        isSameDay(dateRangeValue.value.dateFrom, day.date)) ||
      (dateRangeValue.value?.dateTo &&
        isSameDay(dateRangeValue.value.dateTo, day.date) &&
        !isSelectingRangeSecondDate.value)
    );
  }

  if (!value.value?.date) {
    return false;
  }

  return isSameDay(value.value.date as Date, day.date);
};

function getColorPerDate(date: Date): string | null {
  const dayColor = props.dayBackgroundColors.find(
    (dayBackgroundColor) =>
      getDayISOString(dayBackgroundColor.date) === getDayISOString(date)
  );
  if (!dayColor) {
    return null;
  }
  return `${dayColor.color}` ?? null;
}

const ampm = ref<'am' | 'pm'>(
  props.timeFormat === '12h' && (value.value?.time?.h ?? 0) < 12 ? 'am' : 'pm'
);

// Computed properties

const computedDate = computed(() => {
  if (
    props.appearance === 'input-badge' &&
    ((props.mode !== 'date-range' && !selectedDate.value.day?.date) ||
      (props.mode === 'date-range' &&
        !dateRangeValue.value?.dateFrom &&
        !dateRangeValue.value?.dateTo))
  ) {
    return i18n.t('components.datepicker.no-date-selected');
  }

  if (props.mode === 'date-range') {
    const dateFrom = dateRangeValue.value?.dateFrom
      ? format(dateRangeValue.value?.dateFrom.toString())
      : '';
    const dateTo = dateRangeValue.value?.dateTo
      ? format(dateRangeValue.value?.dateTo.toString())
      : '';
    return `${dateFrom} - ${dateTo}`;
  }

  // Don't show time, if no hour has been selected
  let timeSuffix = '';
  const minutes =
    typeof value.value?.time?.h !== 'undefined' && value.value.time.h > -1
      ? (value.value?.time?.m ?? 0)
      : 0;
  if (typeof value.value?.time?.h !== 'undefined' && value.value.time.h > -1) {
    timeSuffix =
      props.mode === 'date-time'
        ? `, ${formatTime(value.value.time?.h ?? 0, minutes)}`
        : '';
  }

  return selectedDate.value.day?.date
    ? format(selectedDate.value.day?.date.toString()) + timeSuffix
    : '';
});

const isPopup = computed(() =>
  (['input', 'input-badge'] as DatepickerType[]).includes(props.appearance)
);

// Calendar variables

const showCalendar = ref(false);

const days = reactive<DatepickerDay[]>([]);

let calendar: MonthCalendar;

let currentDate: Date;

// initialize currentDate
if (props.mode === 'date-range') {
  currentDate = dateRangeValue.value?.dateFrom ?? new Date();
} else {
  const preselectedDate = props.preselectedDate
    ? props.preselectedDate
    : props.preselectedMonth;

  // If we don't have any date selected yet,
  // but we have a minDate that is outside of the current Month,
  // we want to jump to this month
  if (
    !value.value?.date &&
    props.minDate &&
    !isSameMonth(preselectedDate, props.minDate)
  ) {
    currentDate = props.minDate;
  } else {
    currentDate = value.value?.date
      ? (value.value.date as Date)
      : preselectedDate;
  }
}

// Calendar functions

function updateCalendar(callback?: () => void) {
  calendar = new Calendar()
    .year(currentDate.getFullYear())
    .month(currentDate.getMonth());
  days.splice(0, days.length);

  // Days before month
  let startingIndex = calendar.days()[0].date().getDay();

  // If startingIndex is a Sunday, it's 0.
  // But we need to push the previous week as well,
  // because the calendar starts with Monday
  if (startingIndex === 0) {
    startingIndex = 7;
  }
  const monthBefore = calendar.previous();

  let dateIndex = monthBefore.daysInMonth() - (startingIndex - 1);

  for (let i = 1; i < startingIndex; i++) {
    pushDay(monthBefore.days()[dateIndex].date(), true);
    dateIndex = dateIndex + 1;
  }

  // Days of month
  calendar.days().forEach((day: DayCalendar) => {
    pushDay(day.date(), false);
  });

  // Days after month
  const diff = 7 - (days.length % 7);

  if (diff < 7) {
    const monthAfter = calendar.next();

    dateIndex = 0;

    for (let i = 0; i < diff; i++) {
      pushDay(monthAfter.days()[dateIndex].date(), true);
      dateIndex = dateIndex + 1;
    }
  }

  // After changing page, reconstruct currently selected date
  const s1 = selectedDate.value;
  const s2 = secondDate.value;
  if (s1.day) {
    const res = findDayFromDate(s1.day.date);
    if (res) {
      res.day.isSelected = true;
    }
  }

  // Reconstruct pipes from timespan
  if (props.mode === 'date-range' && s2.index !== -1) {
    setTimespan(s1.day ? s1.day.date : null, s2.day ? s2.day.date : null);
  }

  if (callback) {
    callback();
  }
}

function selectDateByDate(date: Date | null) {
  if (!date) {
    return;
  }
  const res = findDayFromDate(date);
  if (res) {
    selectDate(res.day, res.index);
  } else {
    selectDate(
      {
        date,
        isOutsideCurrentMonth: true,
        isToday: false,
        isDisabled: false,
        isSelected: false,
        isPipeLeft: true,
        isPipeRight: true,
      },
      0
    );
  }
}

const isSelectingRangeSecondDate = ref(false);

function selectDate(day: DatepickerDay, index: number, clicked?: boolean) {
  if (
    props.disabled ||
    day.isDisabled ||
    (day.isSelected && props.mode !== 'date-range')
  ) {
    return;
  }

  if (clicked) {
    emit('dayClicked');
  }

  isSelectingRangeSecondDate.value = true;

  if (props.mode === 'date-range' && selectedDate.value.index !== -1) {
    if (secondDate.value.index !== -1) {
      clearDates();
      clearPipes();
    } else if (
      day.date !== selectedDate.value.day?.date ||
      props.allowSingleDayRange
    ) {
      isSelectingRangeSecondDate.value = false;

      if (
        selectedDate.value.day?.date &&
        getNormalizedDate(selectedDate.value.day?.date) <
          getNormalizedDate(day.date)
      ) {
        secondDate.value = { index, day };
      } else if (
        (selectedDate.value.day?.date &&
          getDayISOString(selectedDate.value.day?.date) !==
            getDayISOString(day.date)) ||
        props.allowSingleDayRange
      ) {
        const dateTemp = klona(selectedDate.value);
        selectedDate.value = { index, day };
        setTimeout(() => {
          secondDate.value = dateTemp;
        }, 1);
      } else {
        return;
      }

      day.isSelected = true;
      showCalendar.value = false;
      return;
    } else {
      return;
    }
  }

  clearSelections();
  day.isSelected = true;
  selectedDate.value = {
    index,
    day,
  };

  if (props.mode === 'date') {
    showCalendar.value = false;
  }

  if (
    props.mode !== 'date-range' &&
    day.date.getMonth() !== currentDate.getMonth()
  ) {
    // Switch to selected month in case the user has clicked on the days outside of the month
    currentDate = day.date;
    updateCalendar();
  }
}

function hoverDate(day: DatepickerDay) {
  const s1 = selectedDate.value;
  const s2 = secondDate.value;
  if (
    props.mode !== 'date-range' ||
    !s1.day ||
    s2.day ||
    s1.day.date.toISOString() === day.date.toISOString()
  ) {
    return;
  } // no date selected yet or two dates already selected
  setTimespan(s1.day.date, day.date);
}

const clearSelections = () => days.forEach((day) => (day.isSelected = false));

function clearDates() {
  selectedDate.value = {
    index: -1,
    day: undefined,
  };
  secondDate.value = {
    index: -1,
    day: undefined,
  };
}

function nextMonth() {
  if (!props.disabled) {
    currentDate = getDate(currentDate.getMonth() + 1);
    updateCalendar();
  }
}

function previousMonth() {
  if (!props.disabled) {
    currentDate = getDate(currentDate.getMonth() - 1);
    updateCalendar();
  }
}

// Range selection

const clearPipes = () =>
  days.forEach((day) => {
    day.isPipeLeft = false;
    day.isPipeRight = false;
  });

function setTimespan(dateA: Date | null, dateB: Date | null) {
  if (!dateA || !dateB) {
    return;
  }

  clearSelections();
  clearPipes();

  const foundDayA = findDayFromDate(dateA);
  const foundDayB = findDayFromDate(dateB);
  let endIndex;
  const diffTime = Math.abs(dateA.getTime() - dateB.getTime()); // Difference of dateA and dateB in Milliseconds
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Difference in Days

  if (foundDayA && foundDayB) {
    // Both selections on the same page
    setPipes(foundDayA.index, foundDayB.index);
  } else if (foundDayB !== foundDayA) {
    // Only one day is found
    if (getNormalizedDate(dateA) > getNormalizedDate(dateB) || foundDayA) {
      endIndex = days.length - 1;
    } else {
      endIndex = 0;
    }
    const indexA = foundDayA ? foundDayA.index : foundDayB!.index;
    setPipes(indexA, endIndex);

    days[endIndex].isPipeLeft = true;
    days[endIndex].isPipeRight = true;
  } else if (
    diffDays > days.length &&
    getNormalizedDate(dateA) < getNormalizedDate(currentDate) &&
    getNormalizedDate(dateB) > getNormalizedDate(currentDate)
  ) {
    // Date A and B are outside of current month and current month is inbetween both dates
    endIndex = days.length - 1;

    setPipes(0, endIndex);
    days[0].isPipeLeft = true;
    days[endIndex].isPipeRight = true;
  }

  if (foundDayA) {
    foundDayA.day.isSelected = true;
  }

  if (foundDayB) {
    foundDayB.day.isSelected = true;
  }
}

function setPipes(indexA: number, indexB: number) {
  const reverse = indexB < indexA;

  const dayA = days[indexA];
  dayA.isPipeLeft = reverse;
  dayA.isPipeRight = !reverse;

  const dayB = days[indexB];
  dayB.isPipeLeft = !reverse;
  dayB.isPipeRight = reverse;

  if (reverse) {
    for (let i = indexB + 1; i < indexA; i++) {
      const d = days[i];
      d.isPipeLeft = true;
      d.isPipeRight = true;
    }
  } else {
    for (let i = indexA + 1; i < indexB; i++) {
      const d = days[i];
      d.isPipeLeft = true;
      d.isPipeRight = true;
    }
  }
}

// Helper functions

const getTimeDisplayName = (option: string): string => {
  if (option.length === 1) {
    return '0' + option;
  }

  return option;
};

function formatTime(hours: number, minutes: number): string {
  const suffix = props.timeFormat === '12h' ? ampm.value : '';
  return (
    getTimeDisplayName(hours.toString()) +
    ':' +
    getTimeDisplayName(minutes.toString()) +
    suffix
  );
}

const onClickOutside = () => {
  showCalendar.value = false;
};

// Use this, if you want to normalize a Date and compare it with other Dates
function getDayISOString(date: Date): string {
  const dateWithoutTime = date;
  date.setHours(0, 0, 0, 0);
  return dateWithoutTime.toISOString();
}

function findDayFromDate(
  date: Date | null
): { day: DatepickerDay; index: number } | null {
  if (!date) {
    return null;
  }

  const foundDay = days.find((day) => isSameDay(day.date, date));

  const foundIndex = days.findIndex((day) => isSameDay(day.date, date));

  if (!foundDay || foundIndex < 0) {
    return null;
  }

  return {
    day: foundDay,
    index: foundIndex,
  };
}

function getDate(monthIndex: number): Date {
  return new Date(currentDate.getFullYear(), monthIndex);
}

function pushDay(date: Date, isOutsideCurrentMonth: boolean) {
  const isToday = getDayISOString(date) === getDayISOString(new Date());
  const isPast = getDayISOString(date) < getDayISOString(new Date());
  const isUnderMinDate =
    !!props.minDate &&
    date.getTime() < props.minDate.getTime() &&
    !isSameDay(props.minDate, date);

  const isOverMaxDate =
    !!props.maxDate && date.getTime() > props.maxDate.getTime();

  days.push({
    date,
    isOutsideCurrentMonth,
    isToday,
    isDisabled:
      props.disabled ||
      (isPast && props.disablePast) ||
      isUnderMinDate ||
      isOverMaxDate,
  });
}

const selectToday = () => {
  if (props.disabled) {
    return;
  }
  currentDate = new Date();
  updateCalendar();

  const res = findDayFromDate(currentDate);
  if (res) {
    selectDate(res.day, res.index);

    if (props.mode === 'date-range' && secondDate.value.index !== -1) {
      updateCalendar();
    }
  }
};

const openCalendar = () => {
  if (!props.disabled) {
    showCalendar.value = true;
  }
};

// Calendar initialization

const initCalendar = () => {
  updateCalendar(() => {
    if (props.mode === 'date-range') {
      if (!dateRangeValue.value) {
        return console.warn('v-model:dateRange is missing');
      }
      const to = dateRangeValue.value.dateTo ?? null;
      setTimespan(dateRangeValue.value?.dateFrom ?? null, to);
      selectDateByDate(dateRangeValue.value?.dateFrom ?? null);
      nextTick(() => {
        selectDateByDate(to);
      });
      return;
    }

    if (value.value?.date) {
      selectDateByDate(value.value.date as Date);
    } else if (props.preselectedDate) {
      selectDateByDate(props.preselectedDate);
    }
  });
};

watch(dateRangeValue, (newValue) => {
  if (
    props.mode !== 'date-range' ||
    !selectedDatePickerDate.value.day?.date ||
    !newValue?.dateFrom ||
    !secondSelectedDatePickerDate.value.day?.date ||
    !newValue?.dateTo
  ) {
    return;
  }

  const dateFromInnerValue = new Date(selectedDatePickerDate.value.day?.date);
  const dateFromOuterValue = new Date(newValue?.dateFrom);
  const dateToInnerValue = new Date(
    secondSelectedDatePickerDate.value.day?.date
  );
  const dateToOuterValue = new Date(newValue?.dateTo);

  /* If User is picking first and second Date (changing innerValue),
    the props.dateRange still updates and triggers this watch.
    But we don't want to init the Calendar when the innerValue has been
    changed by the User.
    We only want to init the Calendar, when props.dateRange
    has been updated in the parent component (outerValue).
    This condition checks, if secondDate already exists
    (if not, the user is in the middle of picking the range)
    and if innerValue and outerValue is not the same. Only than, initialize calendar
  */
  if (
    isSecondDateAlreadySet.value &&
    (!isSameDay(dateFromInnerValue, dateFromOuterValue) ||
      !isSameDay(dateToInnerValue, dateToOuterValue))
  ) {
    initCalendar();
  }
});

watch(value, () => {
  if (!value.value?.date) {
    return;
  }

  currentDate = value.value.date as Date;
  updateCalendar();
  selectDateByDate(value.value.date as Date);
});

initCalendar();

const calendarArea = ref();
const stopAutoUpdatePlacement = ref<(() => void) | null>(null);

const popoverClose = ref<(() => void) | null>(null);
const captureClose = (close: () => void) => {
  popoverClose.value = close;
};
const hideFunction = () => {
  if (popoverClose.value) {
    popoverClose.value();
    showCalendar.value = false;
  }
};

const handlePositioning = async () => {
  if (props.appearance === 'static') {
    return; // don't run for static appearance
  }
  if (stopAutoUpdatePlacement.value) {
    stopAutoUpdatePlacement.value();
  }
  stopAutoUpdatePlacement.value = (
    await useSmartPositioning(inputArea, calendarArea, {
      setMaxWidth: false,
      hideOnOutsideClick: true,
      hideFunction,
      computeOptions: {
        placement: 'bottom-start',
        middleware: [
          offset(({ placement }) => {
            return placement.startsWith('top') ? 20 : -12;
          }),
          flip(),
        ],
      },
    }).getSmartPositionWithAutoUpdate()
  ).value;

  // invisible by default to avoid jumping
  calendarArea.value.classList.remove('invisible');
};

handlePositioning();
watch(showCalendar, handlePositioning);

const togglePopover = () => {
  handlePositioning();
};
</script>
